import {
  IAPIError,
  IDataKeys,
  IJSONAPIErrorCodes,
  IJSONAPIItem,
  IMessage,
  IStatus,
} from "shared/types";
import { copy } from "shared/assets";
import { APIService, loggerService } from "shared/services";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { IUserPatchData } from "Auth/types";
import { PATHS } from "App/router";

interface IJSONAPIUser {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIUserCollection {
  [IDataKeys.user]: IJSONAPIUser;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

/*
  Internal authentication service as a layer between client and Cognito.
*/
export class InternalAuthService {
  private static getSourceUrl = () => window.location.origin;

  /*
    Request an email change for current user
  */
  static requestChangeEmail = async (data: IUserPatchData): Promise<IMessage> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.CHANGE_EMAIL,
          data: {
            ...data,
            confirmationUrl: `${InternalAuthService.getSourceUrl()}${
              PATHS.CONFIRM_EMAIL
            }`,
          },
        });

        loggerService.log({
          icon: "✅",
          title: "Request change email",
          body: "Mail sent",
        });
        resolve({
          body: copy.changeEmail.confirm.replace("%s", data.email!),
          status: IStatus.success,
        });
      } catch (error) {
        loggerService.log({
          icon: "❌",
          title: "Request change email",
          body: error,
        });
        const message: IMessage = {
          status: IStatus.error,
          body: copy.errors.general,
        };

        const apiError = error as IAPIError;

        // Email is already taken
        if (
          apiError.errorCodes.includes(IJSONAPIErrorCodes.EmailExistsException)
        ) {
          message.body = copy.changeEmail.errors.emailExists;
        }

        apiError.errorCodes.push(API_ERROR_CODES.POST_CHANGE_EMAIL);

        reject({ error, message });
      }
    });

  static confirmEmail = async (token: string): Promise<IMessage> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.CONFIRM_EMAIL,
          data: { token },
        });

        loggerService.log({
          icon: "✅",
          title: "Confirm email",
          body: "Email changed",
        });

        resolve({
          body: copy.changeEmail.success,
          status: IStatus.success,
        });
      } catch (error) {
        loggerService.log({
          icon: "❌",
          title: "Confirm email",
          body: error,
        });
        const message: IMessage = {
          status: IStatus.error,
          body: copy.errors.general,
        };

        const apiError = error as IAPIError;

        // Error codes
        if (
          apiError.errorCodes.includes(IJSONAPIErrorCodes.EmailExistsException)
        ) {
          message.body = copy.changeEmail.errors.emailExists;
        } else if (
          apiError.errorCodes.includes(
            IJSONAPIErrorCodes.ExpiredCodeException
          ) ||
          apiError.errorCodes.includes(IJSONAPIErrorCodes.InvalidCodeException)
        ) {
          message.body = copy.changeEmail.errors.tokenExpired;
        }

        apiError.errorCodes.push(API_ERROR_CODES.POST_CONFIRM_EMAIL);

        reject({ error, message });
      }
    });

  static getUser = async (userId: string): Promise<IJSONAPIUserCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const user = await APIService.get({
          path: API_ENDPOINTS.USER(userId),
        });

        loggerService.log({
          icon: "✅",
          title: "Get user",
          body: "Success",
        });

        resolve(user);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_USER);
        loggerService.log({
          icon: "❌",
          title: "Get user",
          body: error,
        });

        const message: IMessage = {
          status: IStatus.error,
          body: copy.errors.general,
        };

        reject({ error, message });
      }
    });

  static editUser = async (
    userId: string,
    data: IUserPatchData
  ): Promise<IJSONAPIUserCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const patchedUser = await APIService.patch({
          path: API_ENDPOINTS.USER(userId),
          data,
        });

        loggerService.log({
          icon: "✅",
          title: "Edit user",
          body: "Success",
        });

        resolve(patchedUser);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_USER);

        loggerService.log({
          icon: "❌",
          title: "Edit user",
          body: error,
        });

        const message: IMessage = {
          status: IStatus.error,
          body: copy.errors.general,
        };

        reject({ error, message });
      }
    });

  static deleteAccount = async (userId: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.USER(userId),
        });

        loggerService.log({
          icon: "✅",
          title: "Delete account",
          body: "success",
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_USER);
        loggerService.log({
          icon: "❌",
          title: "Delete account",
          body: error,
        });
        const message: IMessage = {
          status: IStatus.error,
          body: copy.errors.general,
        };

        reject({ error, message });
      }
    });
}
